/* Shared product discovery, bounded bootstrap, and photo-only upload admission. */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WallForgeUploadPolicy = root.WallForgeUploadPolicy || api;
})(typeof window === 'undefined' ? globalThis : window, function (root) {
  'use strict';
  var cache = new Map();
  var flights = new Map();
  var messages = {
    unavailable: 'Photo upload is temporarily unavailable. Please try again.',
    loading: 'Getting photo upload ready…',
    stale: 'Upload settings changed. Your photos are kept. Review them and try again.'
  };
  // Impulse's button display rules override the browser's default [hidden] rule.
  function setHidden(element, hidden) {
    element.hidden = hidden;
    element.style.display = hidden ? 'none' : '';
  }
  function discoveryError(reason, status) {
    return Object.assign(new Error(reason === 'timeout' ? 'Discovery timeout' : 'Discovery failed'), {
      reason: reason, status: status || null,
      retryable: ['network', 'timeout'].includes(reason) || (reason === 'http' && (status === 408 || status === 429 || status >= 500))
    });
  }
  function reportFailure(error, productId, attempts, started) {
    var details = { reason: error && error.reason || 'initialization', http_status: error && error.status || null,
      product_id: productKey(productId), attempts: attempts || 0, elapsed_ms: started ? Date.now() - started : 0 };
    function capture() {
      if (typeof root.reportWallForgeCustomerFlowFailure === 'function') root.reportWallForgeCustomerFlowFailure({
        errorCode: 'UPLOAD_POLICY_UNAVAILABLE', stage: 'upload_policy', uploader: 'photo_policy'
      });
    }
    try {
      if (root.Sentry && typeof root.Sentry.withScope === 'function') root.Sentry.withScope(function (scope) {
        scope.setTag('upload_policy_failure', details.reason);
        scope.setContext('upload_policy', details);
        capture();
      });
      else capture();
    } catch (_) { /* Diagnostics must not prevent retry or change upload admission. */ }
  }
  function productKey(value) { return String(value || '').replace(/^gid:\/\/shopify\/Product\//, ''); }
  function validatePolicy(policy, productId) {
    var capacity = policy && policy.subjectCapacity;
    if (capacity && (capacity.rule !== 'artwork_pets_v1' || capacity.unit !== 'pets'
      || ![[1, 1], [2, 2], [2, 4]].some(function (pair) { return capacity.min === pair[0] && capacity.max === pair[1]; })
      || policy.selectionFileLimit !== Math.min(policy.maxUploadFiles, capacity.max))) throw new Error('Invalid subject capacity');
    if (!policy || policy.contract !== 'wallforge.customer-upload-policy@v1'
      || productKey(policy.productId) !== productKey(productId)
      || !/^[a-f0-9]{64}$/.test(policy.revision)
      || !['legacy', 'whole_sources'].includes(policy.preparationMode)
      || !['single', 'multi'].includes(policy.uploader)
      || !['compose', 'blend', 'filter'].includes(policy.processingType)
      || ![policy.maxUploadFiles, policy.selectionFileLimit, policy.maxFileSizeBytes, policy.maxBatchSizeBytes]
        .every(function (n) { return Number.isSafeInteger(n) && n > 0; })
      || policy.selectionFileLimit > policy.maxUploadFiles
      || !Array.isArray(policy.allowedMimeTypes) || !policy.allowedMimeTypes.length
      || policy.allowedMimeTypes.some(function (type) { return !['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type); })
      || (policy.preparationMode === 'whole_sources' && (!policy.wholeSourceReady || (!capacity && policy.selectionFileLimit !== policy.maxUploadFiles)))
      || (policy.processingType === 'filter' && (policy.uploader !== 'single' || policy.maxUploadFiles !== 1))
      || (policy.preparationMode === 'whole_sources' && policy.processingType !== 'filter' && policy.uploader !== 'multi')) {
      throw new Error('Invalid upload policy');
    }
    return Object.freeze(Object.assign({}, policy, { allowedMimeTypes: Object.freeze(policy.allowedMimeTypes.slice()) }));
  }
  async function request(url, timeout) {
    var controller = new AbortController();
    var timer;
    // Race the entire response, including JSON decoding, even if an adapter ignores abort.
    var deadline = new Promise(function (_, reject) {
      timer = root.setTimeout(function () { reject(discoveryError('timeout')); controller.abort(); }, timeout);
    });
    try {
      return await Promise.race([deadline, (async function () {
        var response;
        try { response = await root.fetch(url, { signal: controller.signal, cache: 'no-store', credentials: 'omit' }); }
        catch (_) { throw discoveryError('network'); }
        if (!response.ok) throw discoveryError('http', response.status);
        try { return await response.json(); }
        catch (_) { throw discoveryError('invalid_json', response.status); }
      })()]);
    } finally { root.clearTimeout(timer); }
  }
  function discover(productId, options) {
    var base = String(root.WallForgeAPI && root.WallForgeAPI.baseUrl || '').replace(/\/+$/, '');
    var key = base + '/' + productKey(productId);
    if (!base || !productKey(productId)) return Promise.reject(new Error('Missing discovery configuration'));
    if (flights.has(key)) return flights.get(key);
    var cached = cache.get(key);
    if (!(options && options.refresh) && cached && cached.expires > Date.now()) return Promise.resolve(cached.value);
    cache.delete(key);
    var flight = (async function () {
      var failure, attempts = 0, started = Date.now();
      for (var attempt = 0; attempt < 2; attempt++) {
        attempts++;
        try {
          // Give a slow mobile connection more time on its one recovery attempt.
          var value = await request(base + '/api/u/personalization?productId=' + encodeURIComponent(productId), attempt ? 8000 : 3000);
          try { value = Object.assign({}, value, { uploadPolicy: validatePolicy(value && value.uploadPolicy, productId) }); }
          catch (_) { throw discoveryError('invalid_policy'); }
          cache.set(key, { value: value, expires: Date.now() + 60000 });
          return value;
        } catch (error) {
          failure = error;
          if (!error.retryable || attempt === 1) break;
          await new Promise(function (resolve) { root.setTimeout(resolve, 400); });
        }
      }
      reportFailure(failure, productId, attempts, started);
      throw failure;
    })();
    flights.set(key, flight);
    flight.finally(function () { if (flights.get(key) === flight) flights.delete(key); }).catch(function () {});
    return flight;
  }
  function fileError(policy, files, allowEmpty) {
    if (!policy) return messages.unavailable;
    if (!files.length) return allowEmpty ? null : 'Add a photo to generate your preview.';
    if (files.length > policy.selectionFileLimit) return 'Choose up to ' + policy.selectionFileLimit + ' photos. Remove a photo to continue.';
    if (files.some(function (f) { return !f || !policy.allowedMimeTypes.includes(f.type) || !Number.isSafeInteger(f.size) || f.size < 1; })) {
      return 'Please choose supported JPG, PNG or WebP images.';
    }
    if (files.some(function (f) { return f.size > policy.maxFileSizeBytes; })) return 'One or more images are too large.';
    if (files.reduce(function (total, f) { return total + f.size; }, 0) > policy.maxBatchSizeBytes) return 'These photos exceed the total upload size limit. Remove a photo to continue.';
    return null;
  }
  function createGate(container, productId, onChange) {
    var policy = container.__wfUploadPolicy || null;
    var notice = root.document.createElement('div');
    notice.setAttribute('role', 'status');
    var label = root.document.createElement('p');
    var retry = root.document.createElement('button');
    retry.type = 'button'; retry.className = 'btn btn--secondary'; retry.textContent = 'Try again';
    notice.append(label, retry); container.prepend(notice);
    var loading = false;
    function render(error) {
      setHidden(notice, Boolean(policy));
      label.textContent = error ? messages.unavailable : messages.loading;
      setHidden(retry, !error);
      container.querySelectorAll('input[type="file"]').forEach(function (input) { input.disabled = !policy; });
      if (onChange) onChange(policy);
    }
    async function refresh(force) {
      if (loading) return;
      loading = true; policy = null; render(false);
      try { policy = (await discover(productId, { refresh: Boolean(force) })).uploadPolicy; }
      catch (_) { policy = null; }
      finally { loading = false; render(!policy); }
      return policy;
    }
    retry.addEventListener('click', function () { refresh(true); });
    ['click', 'change', 'drop', 'paste'].forEach(function (type) {
      container.addEventListener(type, function (event) {
        if (!policy && !notice.contains(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, true);
    });
    // Let inline uploader initialization finish before invoking its render callback.
    Promise.resolve().then(function () { if (policy) render(false); else refresh(false); });
    return {
      ready: function () { return Boolean(policy); },
      snapshot: function () { return policy; },
      error: function (files, allowEmpty) { return fileError(policy, files, allowEmpty); },
      refresh: function () { return refresh(true); }
    };
  }
  async function mount(router) {
    if (!router || router.dataset.wfRouterMounted || router.dataset.wfRouterLoading) return;
    router.dataset.wfRouterLoading = 'true';
    var notice = router.querySelector('[data-policy-notice]');
    var retry = router.querySelector('[data-policy-retry]');
    notice.textContent = messages.loading; setHidden(retry, true);
    try {
      var policy = (await discover(router.dataset.productId)).uploadPolicy;
      if (!router.isConnected) return;
      var template = router.querySelector('template[data-uploader="' + policy.uploader + '"]');
      var fragment = template.content.cloneNode(true);
      var uploader = fragment.querySelector('[data-wf-uploader-instance]');
      uploader.__wfUploadPolicy = policy;
      // Templates contain only this theme's own markup/scripts, never API HTML.
      var scripts = Array.from(fragment.querySelectorAll('script')).filter(function (script) {
        return !script.type || ['text/javascript', 'application/javascript', 'module'].includes(script.type);
      });
      router.appendChild(fragment);
      for (var original of scripts) {
        var script = root.document.createElement('script');
        Array.from(original.attributes).forEach(function (attr) { script.setAttribute(attr.name, attr.value); });
        script.textContent = original.textContent;
        if (script.src) {
          await new Promise(function (resolve, reject) { script.onload = resolve; script.onerror = reject; original.replaceWith(script); });
        } else original.replaceWith(script);
      }
      router.dataset.wfRouterMounted = 'true';
      setHidden(notice, true);
      router.querySelectorAll(':scope > template').forEach(function (node) { node.remove(); });
      router.dispatchEvent(new CustomEvent('wallforge:uploader-mounted', { bubbles: true, detail: { instanceId: uploader.dataset.wfUploaderInstance } }));
    } catch (_) {
      // A partially initialized uploader must never be appended a second time.
      if (router.querySelector('[data-wf-uploader-instance]')) {
        notice.textContent = 'Please refresh the page to load photo upload.';
        router.querySelector('[data-wf-uploader-instance]').inert = true;
        reportFailure();
      } else {
        notice.textContent = messages.unavailable; setHidden(retry, false);
        retry.onclick = function () { mount(router); };
      }
    } finally { delete router.dataset.wfRouterLoading; }
  }
  return { discover: discover, validatePolicy: validatePolicy, fileError: fileError, createGate: createGate, mount: mount, messages: messages };
});
